import { FieldTypeIcon } from '@components/Customization/fieldTypes';
import {
  closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { isAlwaysAnswered } from '@lib/customization/poiRules';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import {
  Box, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography,
} from '@mui/material';
import {
  resolveLabel, useGetEffectivePoiTypeQuery,
  type InheritedFrom, type PoiField, type PoiFieldGroup, type PoiType, type ResolvedItem,
} from '@store/api/poiTypeEndpoints';
import { useState } from 'react';
import {
  buttonStyles, countPillStyles, dangerHoverStyles, dashedButtonStyles, fieldTableStyles, groupHeaderStyles,
  groupStyles, inheritBannerStyles, inheritedRegionHeaderStyles, inheritedStyles, requiredChipStyles,
} from '@pages/CustomPoiTypes.styles';
import {
  AngleDownIcon, AngleRightIcon, CodeBranchIcon, EditIcon, LayerGroupIcon, LockIcon, PlusIcon, SitemapIcon,
  TrashIcon,
} from 'react-line-awesome';
import { type GroupTarget } from './FieldDrawer';
import { SortableBlock, SortableRow } from './Sortable';

/**
 * Everything one ancestor contributes, as one region of the page.
 *
 * `items` is ordered by ancestor, so a type's whole contribution arrives contiguous and a
 * region is just that stretch. Regioning is what lets the source be named once instead of
 * on every group header and above every field table.
 */
interface InheritedRegion {
  source: InheritedFrom;
  entries: ResolvedItem[];
}

/**
 * Which list an entry belongs to and where it sits in it, as one draggable id.
 *
 * Dragging is confined to one list at a time — ungrouped fields among themselves, groups
 * among themselves, a group's fields among themselves — so a drop whose two ends disagree
 * on the list is dropped rather than guessed at. The position rather than the entry id is
 * what is encoded: a field that has never been saved has no id yet, and two of them would
 * otherwise share `undefined`.
 */
const fieldDragId = (group: GroupTarget, index: number) => `f|${group}|${index}`;
const groupDragId = (index: number) => `g|${index}`;

/** The fields and groups of one type, as shown on its own page. */
interface Props {
  poiType: PoiType;
  /** Resolved name of the type this one extends, when it is one the reader can see. */
  parentName?: string;
  onOpenParent?: () => void;
  onAddField: (group: GroupTarget) => void;
  onEditField: (group: GroupTarget, index: number) => void;
  onDeleteField: (group: GroupTarget, index: number) => void;
  onReorderField: (group: GroupTarget, from: number, to: number) => void;
  onAddGroup: () => void;
  onEditGroup: (index: number) => void;
  onDeleteGroup: (index: number) => void;
  onReorderGroup: (from: number, to: number) => void;
}

const PoiTypeSchema: React.FC<Props> = ({
  poiType, parentName, onOpenParent, onAddField, onEditField, onDeleteField, onReorderField,
  onAddGroup, onEditGroup, onDeleteGroup, onReorderGroup,
}) => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();

  // Collapse is per group and lives here: it is view state, so it must not survive a
  // refetch as if it were part of the type.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const canEdit = has(poiType, 'poi_type:put');

  // A small distance before a drag starts, so pressing the handle and releasing is still a
  // click rather than a drag of zero length. The keyboard sensor is what makes the handle
  // usable without a pointer at all: focus it, space, arrows, space.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Only a type that extends another needs the resolved view; a standalone type already
  // carries everything it has.
  const { currentData } = useGetEffectivePoiTypeQuery(poiType._id, {
    skip: !poiType.inheritFrom,
  });

  // `data` is not scoped to the hook argument — it holds the last result for *any* arg, so
  // walking from a type to its parent left the child's inherited fields rendered on the
  // parent's page. `currentData` is arg-scoped; the id check states the requirement
  // outright rather than relying on remembering which of the two is which.
  const effective = currentData?.id === poiType._id ? currentData : undefined;

  // Rendered in the API's own order rather than partitioned by kind. `items` states the
  // order a device draws in, and a page that regrouped it put every ancestor's fields in
  // one table at the top, detached from that ancestor's groups — so the page disagreed
  // with the contract it is showing.
  const inherited = effective?.items.filter((entry) => entry.inheritedFrom) ?? [];

  const inheritedRegions = inherited.reduce<InheritedRegion[]>((regions, entry) => {
    const current = regions[regions.length - 1];

    if (current && current.source?.id === entry.inheritedFrom?.id) {
      current.entries.push(entry);
    } else {
      regions.push({ source: entry.inheritedFrom, entries: [entry] });
    }

    return regions;
  }, []);

  const inheritedCount = inherited.reduce(
    (total, entry) => total + (entry.kind === 'field' ? 1 : entry.group.fields.length),
    0,
  );

  const ownFieldCount = poiType.fields.length
    + poiType.groups.reduce((total, group) => total + group.fields.length, 0);

  const label = (text: Parameters<typeof resolveLabel>[0]) => resolveLabel(text, locales);

  const toggleGroup = (key: string) => setCollapsed((current) => {
    const next = new Set(current);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  /**
   * A drop only means something inside one list. Both ends carry which list they came
   * from, so a drag that crossed one — out of a group, or onto an inherited block, which
   * registers nothing at all — simply does not move anything. Moving a field to another
   * group stays where it was: the group picker in the field drawer.
   */
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const [kind, scope, index] = String(active.id).split('|');
    const [overKind, overScope, overIndex] = String(over.id).split('|');

    if (kind !== overKind) return;

    if (kind === 'g') {
      onReorderGroup(Number(scope), Number(overScope));
      return;
    }

    if (scope !== overScope) return;

    onReorderField(scope === 'd' ? 'd' : Number(scope), Number(index), Number(overIndex));
  };

  /** One-line summary of a field's configuration, so the table says what the type does. */
  const summary = (field: PoiField): string => {
    switch (field.type) {
      case 'List':
      case 'MultiList':
        return (field.options ?? []).map((option) => label(option.label)).join(' · ') || '—';
      case 'Range':
        return `${field.min ?? '−'} – ${field.max ?? '−'}${field.decimal ? ' · dec' : ''}`
          + (field.value === undefined ? '' : ` · ${field.value}`);
      case 'Barcode':
        return (field.formats ?? []).join(', ') || t('Customization_AnyFormat');
      case 'Text':
        return field.multiline ? t('Customization_Multiline') : t('Customization_SingleLine');
      case 'Number':
        return field.decimal ? t('Customization_Decimal') : t('Customization_Integer');
      case 'Toggle':
        return field.value ? t('Customization_DefaultOn') : t('Customization_DefaultOff');
      default:
        return '—';
    }
  };

  /** The cells of a field row, without the row: a sortable one supplies its own. */
  /**
   * A marker for an entry whose behaviour is conditional, sitting beside the label rather
   * than in `summary` — that cell digests the *type's* configuration, and a rule is
   * orthogonal to it. Placed inside the label cell so the five-cell layout is untouched.
   */
  const ruleBadge = (entry: { rules?: unknown[] }) => (entry.rules?.length
    ? (
      <Tooltip title={t('Customization_RuleCount', { count: entry.rules.length })}>
        <Box component="span" sx={{ color: 'primary.main', display: 'inline-flex', fontSize: 14 }}>
          <CodeBranchIcon />
        </Box>
      </Tooltip>
    )
    : null);

  const fieldCells = (field: PoiField, actions: React.ReactNode, handle: React.ReactNode = null) => (
    <>
      <TableCell sx={{ width: '30%' }}>
        {handle}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{label(field.label)}</Typography>
          {ruleBadge(field)}
        </Stack>
        {field.help && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {field.help}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ width: 150 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FieldTypeIcon type={field.type} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {t(`Customization_FieldType_${field.type}`)}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 108 }}>
        {/* A type that always holds an answer is neither: "Optional" would suggest it can
            be left blank, and it cannot be required. */}
        {isAlwaysAnswered(field.type)
          ? <Typography variant="body2" color="text.secondary">—</Typography>
          : (
            <Chip
              size="small"
              sx={requiredChipStyles}
              variant={field.required ? 'filled' : 'outlined'}
              color={field.required ? 'error' : 'default'}
              label={field.required ? t('Customization_Required') : t('Common_Optional')}
            />
          )}
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {summary(field)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ width: 92 }}>{actions}</TableCell>
    </>
  );

  const inheritedRow = (field: PoiField, key: string, source: string) => (
    <TableRow key={key} hover>{fieldCells(field, lockedAction(source))}</TableRow>
  );

  /** A field of this type: draggable within its own list, unless there is nothing to move. */
  const ownRow = (field: PoiField, key: string, group: GroupTarget, index: number, count: number) => (
    <SortableRow
      key={key}
      id={fieldDragId(group, index)}
      disabled={!canEdit || count < 2}
      handleTitle={t('Customization_Reorder')}
    >
      {(handle) => fieldCells(field, ownActions(group, index), handle)}
    </SortableRow>
  );

  /** Same shape as the detail header's actions: an icon, a tooltip, danger only on delete. */
  const iconAction = (
    title: string, allowed: boolean, icon: React.ReactNode, onClick: () => void, danger = false
  ) => (
    <Tooltip title={allowed ? title : t('Permission_NotAllowed')} arrow>
      <span>
        <IconButton
          size="small"
          disabled={!allowed}
          onClick={onClick}
          sx={danger ? dangerHoverStyles : { color: 'text.secondary' }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const ownActions = (group: GroupTarget, index: number) => (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      {iconAction(t('Common_Edit'), canEdit, <EditIcon />, () => onEditField(group, index))}
      {iconAction(t('Common_Delete'), canEdit, <TrashIcon />, () => onDeleteField(group, index), true)}
    </Stack>
  );

  const lockedAction = (source: string) => (
    <Tooltip title={t('Customization_InheritedFrom', [source])} arrow>
      <Box sx={{ color: 'text.disabled', display: 'inline-flex', fontSize: 15 }}>
        <LockIcon />
      </Box>
    </Tooltip>
  );

  const fieldTable = (rows: React.ReactNode) => (
    <Table size="small" sx={fieldTableStyles}>
      <TableBody>{rows}</TableBody>
    </Table>
  );

  /**
   * The inside of a group: its header and its fields. The block around it is the caller's,
   * because an own group is wrapped in a sortable one and an inherited group is not.
   */
  const groupContent = (
    group: PoiFieldGroup, inheritedFromName: string | null, ownIndex: number | null, handle: React.ReactNode = null,
  ) => {
    const key = `${poiType._id}:${group.id ?? ownIndex}`;
    const isCollapsed = collapsed.has(key);

    return (
      <>
        <Stack direction="row" sx={groupHeaderStyles} onClick={() => toggleGroup(key)}>
          {/* Outside the header's click target: pressing the handle starts a drag, it does
              not collapse the group underneath it. */}
          <Box component="span" onClick={(event) => event.stopPropagation()} sx={{ display: 'inline-flex' }}>
            {handle}
          </Box>
          <Box
            sx={{
              color: 'text.disabled',
              display: 'inline-flex',
              fontSize: 16,
              transition: 'transform 150ms ease',
              transform: isCollapsed ? 'rotate(-90deg)' : 'none',
            }}
          >
            <AngleDownIcon />
          </Box>
          <Box sx={{ color: 'text.disabled', display: 'inline-flex', fontSize: 15 }}>
            <LayerGroupIcon />
          </Box>
          <Typography variant="subtitle2">{label(group.name)}</Typography>
          {ruleBadge(group)}
          {group.description && (
            <Typography variant="caption" color="text.secondary">· {group.description}</Typography>
          )}
          <Box component="span" sx={countPillStyles}>{group.fields.length}</Box>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={0.25} onClick={(event) => event.stopPropagation()}>
            {/* No source chip here: an inherited group sits inside a region already
                headed by the type it came from, and repeating it on every group was the
                noise this replaced. */}
            {inheritedFromName === null && ownIndex !== null && (
              <>
                {iconAction(t('Customization_AddField'), canEdit, <PlusIcon />, () => onAddField(ownIndex))}
                {iconAction(t('Customization_RenameGroup'), canEdit, <EditIcon />, () => onEditGroup(ownIndex))}
                {iconAction(t('Customization_DeleteGroup'), canEdit, <TrashIcon />, () => onDeleteGroup(ownIndex), true)}
              </>
            )}
          </Stack>
        </Stack>

        {!isCollapsed && (group.fields.length > 0
          ? fieldTable(inheritedFromName !== null || ownIndex === null
            ? group.fields.map((field, index) => inheritedRow(
              field, `${key}-${field.id ?? index}`, inheritedFromName ?? '',
            ))
            : (
              <SortableContext
                items={group.fields.map((_, index) => fieldDragId(ownIndex, index))}
                strategy={verticalListSortingStrategy}
              >
                {group.fields.map((field, index) => ownRow(
                  field, `${key}-${field.id ?? index}`, ownIndex, index, group.fields.length,
                ))}
              </SortableContext>
            ))
          : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 6.5, pb: 1.5 }}>
              {t('Customization_NoFieldsInGroup')}
            </Typography>
          ))}
      </>
    );
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {poiType.inheritFrom && (
        <Stack direction="row" sx={inheritBannerStyles}>
          <Box sx={{ display: 'inline-flex', fontSize: 15 }}><SitemapIcon /></Box>
          {/* Naming the parent matters more than the count: it answers "where do I go to
              change these fields". Falls back to the count alone when the parent is not
              among the types this reader can see. */}
          <Typography variant="caption" sx={{ flexGrow: 1, minWidth: 0 }}>
            {parentName
              ? t('Customization_ExtendsBanner', { count: inheritedCount, 1: parentName })
              : t('Customization_InheritsBanner', { count: inheritedCount })}
          </Typography>
          {parentName && onOpenParent && (
            <Button size="small" color="inherit" endIcon={<AngleRightIcon />} onClick={onOpenParent} sx={buttonStyles}>
              {t('Customization_OpenParent')}
            </Button>
          )}
        </Stack>
      )}

      {/* Inherited content first and locked, one region per ancestor, oldest first — the
          order `items` resolves in. Within a region the API emits that type's ungrouped
          fields before its groups, so rendering the table above the sections reproduces
          the resolved order rather than reordering it. */}
      {inheritedRegions.map((region) => {
        const sourceName = label(region.source?.name);
        const regionFields = region.entries.filter((entry) => entry.kind === 'field');
        const regionGroups = region.entries.filter((entry) => entry.kind === 'group');

        return (
          <Box key={`region-${region.source?.id}`} sx={{ ...groupStyles, ...inheritedStyles }}>
            <Stack direction="row" sx={inheritedRegionHeaderStyles}>
              <Box sx={{ color: 'primary.main', display: 'inline-flex', fontSize: 14 }}>
                <SitemapIcon />
              </Box>
              <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }} noWrap>
                {sourceName}
              </Typography>
            </Stack>

            {regionFields.length > 0 && fieldTable(regionFields.map((entry, index) => inheritedRow(
              entry.field, `inh-${entry.field.id ?? index}`, sourceName,
            )))}

            {regionGroups.map((entry, index) => (
              <Box key={`inh-group-${entry.group.id ?? index}`} sx={groupStyles}>
                {groupContent(entry.group, sourceName, null)}
              </Box>
            ))}
          </Box>
        );
      })}

      {/* One context for the whole card: each list registers its own entries, and a drop
          that lands outside the list it started in moves nothing. */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        {poiType.fields.length > 0 && (
          <Box sx={groupStyles}>
            {fieldTable(
              <SortableContext
                items={poiType.fields.map((_, index) => fieldDragId('d', index))}
                strategy={verticalListSortingStrategy}
              >
                {poiType.fields.map((field, index) => ownRow(
                  field, `own-${field.id ?? index}`, 'd', index, poiType.fields.length,
                ))}
              </SortableContext>
            )}
          </Box>
        )}

        <SortableContext
          items={poiType.groups.map((_, index) => groupDragId(index))}
          strategy={verticalListSortingStrategy}
        >
          {poiType.groups.map((group, index) => (
            <SortableBlock
              key={`${poiType._id}:${group.id ?? index}`}
              id={groupDragId(index)}
              disabled={!canEdit || poiType.groups.length < 2}
              handleTitle={t('Customization_Reorder')}
              sx={groupStyles}
            >
              {(handle) => groupContent(group, null, index, handle)}
            </SortableBlock>
          ))}
        </SortableContext>
      </DndContext>

      {ownFieldCount === 0 && poiType.groups.length === 0 && inheritedCount === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2.25, py: 3, textAlign: 'center' }}>
          {t('Customization_NoFieldsYet')}
        </Typography>
      )}

      <Stack direction="row" spacing={1.25} sx={{ px: 2.25, py: 1.75, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button size="small" startIcon={<PlusIcon />} disabled={!canEdit} sx={dashedButtonStyles} onClick={() => onAddField('d')}>
          {t('Customization_AddField')}
        </Button>
        <Button size="small" startIcon={<PlusIcon />} disabled={!canEdit} sx={dashedButtonStyles} onClick={onAddGroup}>
          {t('Customization_AddGroup')}
        </Button>
      </Stack>
    </Paper>
  );
};

export default PoiTypeSchema;
